import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import {
  Package, TrendingUp, DollarSign, Wrench, Gavel, AlertCircle, Receipt,
  ArrowRight, ShoppingCart, Users as UsersIcon,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  Area, AreaChart,
} from "recharts";

const TOOLTIP_STYLE = {
  background: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 12,
  fontSize: 12,
  padding: "8px 12px",
};
const TOOLTIP_FMT = (v) => `$${Number(v).toLocaleString()}`;
const BAR_RADIUS = [8, 8, 0, 0];
const AGING_BUCKETS = [
  { key: "0-30", label: "0–30 days", color: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
  { key: "31-60", label: "31–60 days", color: "bg-blue-500", text: "text-blue-600 dark:text-blue-400" },
  { key: "61-90", label: "61–90 days", color: "bg-amber-500", text: "text-amber-600 dark:text-amber-400" },
  { key: "90+", label: "90+ days", color: "bg-red-500", text: "text-red-600 dark:text-red-400" },
];

const Kpi = ({ icon: Icon, label, value, sub, accent, gradient, testid }) => (
  <div data-testid={testid} className="surface p-5 sm:p-6 relative overflow-hidden group surface-hover">
    {gradient && <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full ${gradient} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity`} />}
    <div className="flex items-start justify-between mb-4 relative">
      <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</div>
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 dark:from-blue-400/15 dark:to-blue-600/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
        <Icon size={18} strokeWidth={1.75} />
      </div>
    </div>
    <div className={`font-display text-3xl sm:text-4xl font-bold tracking-tight relative ${accent || ""}`}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1.5 relative">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return (
    <div className="space-y-4">
      {[1,2].map((i) => <div key={i} className="surface p-6 animate-pulse h-32" />)}
    </div>
  );

  const profitColor = (data.gross_profit || 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400";
  const netColor = (data.net_profit || 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400";

  return (
    <div data-testid="dashboard-page">
      <PageHeader overline="Control Room" title="Dashboard" description="Real-time snapshot of inventory, sales, expenses and profitability." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <Kpi testid="kpi-inventory-cost" icon={Package} label="Inventory Value" value={fmtMoney(data.inventory_cost)} sub={`${data.in_stock_count} vehicles in stock`} gradient="bg-blue-500" />
        <Kpi testid="kpi-gross-profit" icon={TrendingUp} label="Gross Profit" value={fmtMoney(data.gross_profit)} accent={profitColor} sub={`${data.sold_count} cars sold all-time`} gradient="bg-emerald-500" />
        <Kpi testid="kpi-net-profit" icon={DollarSign} label="Net Profit" value={fmtMoney(data.net_profit)} accent={netColor} sub="Incl. fees received" gradient="bg-emerald-500" />
        <Kpi testid="kpi-roi" icon={TrendingUp} label="ROI / Vehicle" value={fmtPct(data.roi_per_vehicle)} accent={profitColor} sub={fmtMoney(data.avg_profit_per_vehicle) + " avg profit"} gradient="bg-amber-500" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4 stagger">
        <Kpi testid="kpi-month-sales" icon={ShoppingCart} label="Sold This Month" value={data.month_sales_count} sub="Closed deals" />
        <Kpi testid="kpi-outstanding" icon={AlertCircle} label="Outstanding" value={fmtMoney(data.outstanding_payments)} sub="Customer balances" accent={data.outstanding_payments > 0 ? "text-amber-600 dark:text-amber-400" : ""} />
        <Kpi testid="kpi-pending-repairs" icon={Wrench} label="Pending Repairs" value={data.pending_repairs} sub="In service bay" />
        <Kpi testid="kpi-auctions-month" icon={Gavel} label="Auction Buys" value={data.auction_purchases_month} sub="This month" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4 stagger">
        <Kpi testid="kpi-in-stock" icon={Package} label="In Stock" value={data.in_stock_count} sub="Active inventory" />
        <Kpi testid="kpi-sold" icon={Receipt} label="Total Sold" value={data.sold_count} sub="Lifetime" />
        <Kpi testid="kpi-customers" icon={UsersIcon} label="Customers" value={data.customers_count} sub="In CRM" />
        <Kpi testid="kpi-total-vehicles" icon={Package} label="Total Vehicles" value={data.total_vehicles} sub="All-time" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Monthly Revenue</div>
              <h3 className="font-display text-xl font-semibold mt-1">Sales — last 6 months</h3>
            </div>
            <span className="chip bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">Revenue</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.revenue_series}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={TOOLTIP_FMT} />
              <Area type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2.5} fill="url(#revGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Monthly Expenses</div>
              <h3 className="font-display text-xl font-semibold mt-1">Overhead — last 6 months</h3>
            </div>
            <span className="chip bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300">Expenses</span>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.expense_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={TOOLTIP_FMT} />
              <Bar dataKey="expense" fill="#f59e0b" radius={BAR_RADIUS} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Aging + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Inventory Aging</div>
              <h3 className="font-display text-xl font-semibold mt-1">Days in stock</h3>
            </div>
            <Link to="/inventory" className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">View all <ArrowRight size={12} /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {AGING_BUCKETS.map((b) => (
              <div key={b.key} data-testid={`aging-${b.key}`} className="surface p-4">
                <div className={`h-1.5 ${b.color} rounded-full mb-4`} />
                <div className="text-xs text-muted-foreground">{b.label}</div>
                <div className={`font-display text-3xl font-bold mt-1 ${b.text}`}>{data.aging[b.key]}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Alerts</div>
          <h3 className="font-display text-xl font-semibold mt-1 mb-4">Needs attention</h3>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between p-3 rounded-xl bg-amber-50/80 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300" data-testid="alert-aging">
              <div className="flex items-center gap-2 text-sm font-medium"><AlertCircle size={14} /> Over 60 days in stock</div>
              <span className="font-bold text-lg">{data.alerts.over_60_days.length}</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-red-50/80 dark:bg-red-500/10 text-red-700 dark:text-red-300" data-testid="alert-titles">
              <div className="flex items-center gap-2 text-sm font-medium"><AlertCircle size={14} /> Missing titles</div>
              <span className="font-bold text-lg">{data.alerts.missing_titles.length}</span>
            </div>
            <Link to="/inventory" className="block text-center py-2.5 mt-2 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">Review inventory →</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
